export interface Point {
  x: number;
  y: number;
}

export interface BoundingBoxLike {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

export interface DropCandidate {
  id: string;
  box: BoundingBoxLike;
  isParent: boolean;
  depth: number;
}

export const DROP_TARGET_MAX_DISTANCE = 5;
export const DROP_TARGET_PARENT_PADDING = 12;
export const DROP_TARGET_LEAF_PADDING = 5;

export function distanceToBoundingBox(point: Point, bb: BoundingBoxLike, padding = 0): number {
  const adjustedBB = {
    left: bb.x1 + padding,
    right: bb.x2 + padding,
    top: bb.y1 + padding,
    bottom: bb.y2 - padding
  };

  const dx = Math.max(adjustedBB.left - point.x, 0, point.x - adjustedBB.right);
  const dy = Math.max(adjustedBB.top - point.y, 0, point.y - adjustedBB.bottom);

  return Math.sqrt(dx * dx + dy * dy);
}

export function dropCandidatePadding(candidate: Pick<DropCandidate, 'isParent'>): number {
  return candidate.isParent ? DROP_TARGET_PARENT_PADDING : DROP_TARGET_LEAF_PADDING;
}

/**
 * Decides which candidate a drag is being dropped into, purely from geometry.
 * `point` is the cursor in rendered (screen) coordinates; candidate boxes are
 * rendered bounding boxes without labels.
 */
export function pickDropTarget(
  point: Point,
  candidates: DropCandidate[],
  maxDistance = DROP_TARGET_MAX_DISTANCE
): string | undefined {
  let closest: DropCandidate | null = null;
  let closestDistance = Number.POSITIVE_INFINITY;

  candidates.forEach((candidate) => {
    const distance = distanceToBoundingBox(point, candidate.box, dropCandidatePadding(candidate));
    const isDeeper = candidate.depth > 0 && (!closest || candidate.depth > closest.depth);

    if (distance < closestDistance || (distance === closestDistance && isDeeper)) {
      closestDistance = distance;
      closest = candidate;
    }
  });

  return closest && closestDistance <= maxDistance ? (closest as DropCandidate).id : undefined;
}
